import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Book,
  Video,
  FileText,
  Lightbulb,
  Search,
  Bookmark,
  BookmarkCheck,
  Plus,
  ExternalLink,
  Sparkles,
  X,
  Trash2,
  Loader2,
  Users,
  Shield,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const RESOURCE_TYPES = [
  { value: "all", label: "All", icon: null },
  { value: "book", label: "Books", icon: Book },
  { value: "video", label: "Videos", icon: Video },
  { value: "article", label: "Articles", icon: FileText },
  { value: "tip", label: "Tips", icon: Lightbulb },
];

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "writing", label: "Writing" },
  { value: "performance", label: "Performance" },
  { value: "business", label: "Business" },
  { value: "mindset", label: "Mindset" },
  { value: "general", label: "General" },
];

const SOURCE_FILTERS = [
  { value: "all", label: "All Sources", icon: null },
  { value: "admin", label: "Curated", icon: Shield },
  { value: "user", label: "Community", icon: Users },
  { value: "ai", label: "AI Picks", icon: Brain },
];

export default function ResourceLibrary({ isOpen, onClose }) {
  const [resources, setResources] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [showBookmarked, setShowBookmarked] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newResource, setNewResource] = useState({
    title: "",
    description: "",
    resource_type: "article",
    url: "",
    author: "",
    category: "general",
  });

  // Fetch resources
  const fetchResources = async () => {
    try {
      setIsLoading(true);
      let url = `${API}/resources`;
      const params = new URLSearchParams();
      
      if (selectedType !== "all") params.append("resource_type", selectedType);
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (selectedSource !== "all") params.append("source", selectedSource);
      if (searchQuery) params.append("search", searchQuery);
      if (showBookmarked) params.append("bookmarked", "true");
      
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error("Failed to fetch resources:", error);
      toast.error("Failed to load resources");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch AI recommendations
  const fetchAIRecommendations = async () => {
    try {
      setIsLoadingAI(true);
      const response = await fetch(`${API}/resources/ai-recommend`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAiRecommendations(data);
        toast.success("AI recommendations generated!");
      }
    } catch (error) {
      console.error("Failed to fetch AI recommendations:", error);
      toast.error("Failed to generate recommendations");
    } finally {
      setIsLoadingAI(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchResources();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedType, selectedCategory, selectedSource, searchQuery, showBookmarked]);

  // Toggle bookmark
  const handleToggleBookmark = async (resource) => {
    try {
      const url = `${API}/resources/${resource.resource_id}/bookmark`;
      const method = resource.is_bookmarked ? "DELETE" : "POST";
      
      const response = await fetch(url, { method, credentials: "include" });
      if (response.ok) {
        // Update local state
        setResources(prev =>
          prev.map(r =>
            r.resource_id === resource.resource_id
              ? { ...r, is_bookmarked: !r.is_bookmarked }
              : r
          )
        );
        toast.success(resource.is_bookmarked ? "Bookmark removed" : "Resource bookmarked!");
      }
    } catch (error) {
      toast.error("Failed to update bookmark");
    }
  };

  // Submit new resource
  const handleSubmitResource = async () => {
    if (!newResource.title || !newResource.description) {
      toast.error("Title and description are required");
      return;
    }

    try {
      const response = await fetch(`${API}/resources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newResource),
      });

      if (response.ok) {
        toast.success("Resource submitted!");
        setIsAddDialogOpen(false);
        setNewResource({
          title: "",
          description: "",
          resource_type: "article",
          url: "",
          author: "",
          category: "general",
        });
        fetchResources();
      }
    } catch (error) {
      toast.error("Failed to submit resource");
    }
  };

  // Delete resource
  const handleDeleteResource = async (resourceId) => {
    try {
      const response = await fetch(`${API}/resources/${resourceId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Resource deleted");
        fetchResources();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Cannot delete this resource");
      }
    } catch (error) {
      toast.error("Failed to delete resource");
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = RESOURCE_TYPES.find(t => t.value === type);
    const Icon = typeConfig?.icon || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const getSourceBadge = (source) => {
    if (source === "admin") return <Badge variant="outline" className="text-xs border-primary text-primary"><Shield className="w-3 h-3 mr-1" />Curated</Badge>;
    if (source === "user") return <Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" />Community</Badge>;
    if (source === "ai") return <Badge variant="outline" className="text-xs border-purple-500 text-purple-500"><Brain className="w-3 h-3 mr-1" />AI</Badge>;
    return null;
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      writing: "bg-blue-500/20 text-blue-400",
      performance: "bg-green-500/20 text-green-400",
      business: "bg-amber-500/20 text-amber-400",
      mindset: "bg-purple-500/20 text-purple-400",
      general: "bg-gray-500/20 text-gray-400",
    };
    return colors[category] || colors.general;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] h-[90vh] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="font-display text-2xl uppercase tracking-wider flex items-center gap-2">
            <Book className="w-6 h-6 text-primary" />
            Resource Library
          </DialogTitle>
          <DialogDescription>
            Books, videos, articles, and tips to level up your comedy game
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden p-6 pt-4">
          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-muted border-0"
                data-testid="resource-search"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[150px]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={showBookmarked ? "default" : "outline"}
              onClick={() => setShowBookmarked(!showBookmarked)}
              className="gap-2"
              data-testid="bookmarks-toggle"
            >
              {showBookmarked ? (
                <BookmarkCheck className="w-4 h-4" />
              ) : (
                <Bookmark className="w-4 h-4" />
              )}
              Bookmarks
            </Button>
          </div>

          {/* Tabs for resource types */}
          <Tabs value={selectedType} onValueChange={setSelectedType} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <TabsList className="bg-muted">
                {RESOURCE_TYPES.map((type) => (
                  <TabsTrigger key={type.value} value={type.value} className="gap-2" data-testid={`tab-${type.value}`}>
                    {type.icon && <type.icon className="w-4 h-4" />}
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={fetchAIRecommendations}
                  disabled={isLoadingAI}
                  className="gap-2"
                  data-testid="ai-recommend-btn"
                >
                  {isLoadingAI ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  AI Recommend
                </Button>
                <Button
                  onClick={() => setIsAddDialogOpen(true)}
                  className="gap-2 bg-primary hover:bg-primary/90"
                  data-testid="add-resource-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Resource
                </Button>
              </div>
            </div>

            {/* Source filter pills */}
            <div className="flex gap-2 mb-4">
              {SOURCE_FILTERS.map((source) => (
                <button
                  key={source.value}
                  onClick={() => setSelectedSource(source.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedSource === source.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80 text-muted-foreground"
                  }`}
                  data-testid={`source-${source.value}`}
                >
                  {source.icon && <source.icon className="w-3 h-3" />}
                  {source.label}
                </button>
              ))}
            </div>

            {/* AI Recommendations Section */}
            {aiRecommendations.length > 0 && (
              <div className="mb-4 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-purple-400 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    AI Recommendations for You
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAiRecommendations([])}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {aiRecommendations.map((rec, idx) => (
                    <div
                      key={rec.resource_id || idx}
                      className="p-3 bg-background/50 rounded-md border border-border"
                    >
                      <h4 className="font-medium text-sm mb-1">{rec.title}</h4>
                      <p className="text-xs text-muted-foreground">{rec.description}</p>
                      <Badge className={`mt-2 text-xs ${getCategoryBadgeColor(rec.category)}`}>
                        {rec.category}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resources Grid */}
            <TabsContent value={selectedType} className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-[350px]">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : resources.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <Book className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="font-display text-lg uppercase mb-2">No Resources Found</h3>
                    <p className="text-muted-foreground text-sm max-w-sm">
                      {showBookmarked
                        ? "You haven't bookmarked any resources yet."
                        : "Try adjusting your filters or add a new resource."}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 pr-4">
                    {resources.map((resource) => (
                      <div
                        key={resource.resource_id}
                        className="group p-4 bg-card border border-border rounded-sm hover:border-primary/50 transition-colors"
                        data-testid={`resource-${resource.resource_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            {getTypeIcon(resource.resource_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium text-sm line-clamp-1">{resource.title}</h4>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleToggleBookmark(resource)}
                                  data-testid={`bookmark-${resource.resource_id}`}
                                >
                                  {resource.is_bookmarked ? (
                                    <BookmarkCheck className="w-4 h-4 text-primary" />
                                  ) : (
                                    <Bookmark className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </Button>
                                {resource.source === "user" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDeleteResource(resource.resource_id)}
                                    data-testid={`delete-${resource.resource_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            {resource.author && (
                              <p className="text-xs text-muted-foreground mb-1">by {resource.author}</p>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {resource.description}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {getSourceBadge(resource.source)}
                              <Badge className={`text-xs ${getCategoryBadgeColor(resource.category)}`}>
                                {resource.category}
                              </Badge>
                              {resource.url && (
                                <a
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Add Resource Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl uppercase tracking-wider">
                Add Resource
              </DialogTitle>
              <DialogDescription>
                Share a helpful resource with the community
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Title *</label>
                <Input
                  value={newResource.title}
                  onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                  placeholder="Resource title"
                  data-testid="new-resource-title"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Description *</label>
                <Textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                  placeholder="Why is this resource helpful?"
                  rows={3}
                  data-testid="new-resource-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Type</label>
                  <Select
                    value={newResource.resource_type}
                    onValueChange={(value) => setNewResource({ ...newResource, resource_type: value })}
                  >
                    <SelectTrigger data-testid="new-resource-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="book">Book</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="article">Article</SelectItem>
                      <SelectItem value="tip">Tip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Category</label>
                  <Select
                    value={newResource.category}
                    onValueChange={(value) => setNewResource({ ...newResource, category: value })}
                  >
                    <SelectTrigger data-testid="new-resource-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="writing">Writing</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="mindset">Mindset</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Author</label>
                <Input
                  value={newResource.author}
                  onChange={(e) => setNewResource({ ...newResource, author: e.target.value })}
                  placeholder="Author or creator name"
                  data-testid="new-resource-author"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">URL (optional)</label>
                <Input
                  value={newResource.url}
                  onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                  placeholder="https://..."
                  data-testid="new-resource-url"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmitResource} className="bg-primary hover:bg-primary/90">
                  Submit Resource
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
